import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { LoadAcceptanceCard } from '@/components/LoadAcceptanceCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Truck, Calendar, Package, MapPin, CheckCircle, ExternalLink, Building2, Briefcase } from 'lucide-react';
import { SettingsDropdown } from '@/components/SettingsDropdown';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRouteDisplay } from '@/utils/routeUtils';

interface LoadOrderData {
  id: string;
  title: string;
  description?: string;
  status: string;
  vehicles_needed: number;
  vehicles_assigned: number;
  vehicles_completed: number;
  vehicles_available: number;
  cargo_type: string;
  cargo_weight?: number;
  created_at: string;
  customer_org_id: string;
  organizations: {
    name: string;
    username?: string;
  };
  load_routes: Array<{
    id: string;
    title: string;
    pickup_city: string;
    delivery_city: string;
    pickup_date: string;
    delivery_date: string;
    pickup_address: string;
    delivery_address: string;
    vehicles_needed: number;
    vehicles_assigned: number;
    vehicles_completed: number;
    vehicles_available: number;
    status: string;
    vehicle_type?: string;
  }>;
}

interface AcceptanceRecord {
  id: string;
  trucksAccepted: number;
  timestamp: Date;
  status: string;
}

interface CarrierResponse {
  trucksCommitted: number;
  status: "selecting" | "accepted";
}

export const LoadOrderTrackingPage = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { formatRoute } = useRouteDisplay();
  const [loadOrder, setLoadOrder] = useState<LoadOrderData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userOrganizations, setUserOrganizations] = useState<string[]>([]);
  const [isCustomerOrgMember, setIsCustomerOrgMember] = useState(false);
  
  // Acceptance functionality states (only used when authenticated and not customer org member)
  const [responses, setResponses] = useState<Record<string, CarrierResponse>>({});
  const [expandedAcceptance, setExpandedAcceptance] = useState<string | null>(null);
  const [loadingAccept, setLoadingAccept] = useState<string | null>(null);

  useEffect(() => {
    fetchLoadOrder();
    if (user) {
      fetchUserOrganizations();
    }
  }, [id, user]);

  // Real-time subscription for load routes
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel('load-routes-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'load_routes'
        },
        (payload) => {
          console.log('Load route changed:', payload);
          // Refresh load order data when routes change
          fetchLoadOrder();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  const fetchUserOrganizations = async () => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from('organization_members')
        .select('org_id')
        .eq('user_id', user.id);
      
      const orgIds = data?.map(org => org.org_id) || [];
      setUserOrganizations(orgIds);
    } catch (error) {
      console.error('Error fetching user organizations:', error);
    }
  };


  useEffect(() => {
    if (loadOrder && userOrganizations.length > 0) {
      setIsCustomerOrgMember(userOrganizations.includes(loadOrder.customer_org_id));
    }
  }, [loadOrder, userOrganizations]);

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'published': return 'secondary';
      case 'draft': return 'outline';
      default: return 'secondary';
    }
  };

  // Simplified acceptance functionality - most logic is now in LoadAcceptanceCard
  const handleStartSelection = (routeId: string) => {
    // Check if user is authenticated
    if (!user) {
      // Redirect to auth page with returnUrl (use pathname only, not full URL)
      const currentPath = window.location.pathname;
      window.location.href = `/auth?returnUrl=${encodeURIComponent(currentPath)}`;
      return;
    }
    
    const route = loadOrder?.load_routes.find(r => r.id === routeId);
    if (!route) return;
    
    const remainingTrucks = route.vehicles_available;
    if (remainingTrucks <= 0) {
      toast({
        title: "No trucks available",
        description: "This route is fully assigned.",
        variant: "destructive"
      });
      return;
    }

    setResponses(prev => ({
      ...prev,
      [routeId]: {
        status: "selecting",
        trucksCommitted: Math.min(1, remainingTrucks)
      }
    }));
  };

  const updateTrucks = (routeId: string, trucks: number) => {
    const route = loadOrder?.load_routes.find(r => r.id === routeId);
    if (!route) return;
    
    const maxTrucks = route.vehicles_available;
    if (trucks > maxTrucks) {
      toast({
        title: "Invalid truck count",
        description: `Maximum ${maxTrucks} trucks available for this route.`,
        variant: "destructive"
      });
      return;
    }

    setResponses(prev => ({
      ...prev,
      [routeId]: {
        ...prev[routeId],
        trucksCommitted: Math.max(1, Math.min(trucks, maxTrucks))
      }
    }));
  };

  const handleCancelSelection = (routeId: string) => {
    setResponses(prev => {
      const newResponses = { ...prev };
      delete newResponses[routeId];
      return newResponses;
    });
  };

  // Complete and Cancel handlers for carriers
  const handleCompleteRoute = async (routeId: string, count: number) => {
    if (!user) return;
    
    try {
      // Get user's organization
      const { data: userOrgs } = await supabase
        .from('organization_members')
        .select('org_id')
        .eq('user_id', user.id);
      
      if (!userOrgs || userOrgs.length === 0) {
        toast({
          title: "Error",
          description: "You must be part of an organization.",
          variant: "destructive"
        });
        return;
      }

      console.log('=== HANDLE COMPLETE ROUTE ===');
      console.log('Route ID:', routeId);
      console.log('Count:', count);
      console.log('Carrier Org ID:', userOrgs[0].org_id);

      // Use carrier-specific truck operation
      const { error } = await supabase.rpc('handle_carrier_truck_operation', {
        route_id: routeId,
        carrier_org_id: userOrgs[0].org_id,
        operation_type: 'complete',
        truck_count: count
      });

      if (error) {
        console.error('RPC Error:', error);
        throw error;
      }

      console.log('RPC call successful, refreshing data...');

      toast({
        title: "Success",
        description: `${count} truck(s) marked as completed.`,
      });

      // Refresh load data - this will trigger re-render of LoadAcceptanceCard
      // which will re-fetch carrier assignments
      await fetchLoadOrder();
      console.log('Load order refreshed');
    } catch (error) {
      console.error('Complete route error:', error);
      toast({
        title: "Error",
        description: "Failed to complete trucks. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleCancelRoute = async (routeId: string, count: number) => {
    if (!user) return;
    
    try {
      // Get user's organization
      const { data: userOrgs } = await supabase
        .from('organization_members')
        .select('org_id')
        .eq('user_id', user.id);
      
      if (!userOrgs || userOrgs.length === 0) {
        toast({
          title: "Error",
          description: "You must be part of an organization.",
          variant: "destructive"
        });
        return;
      }

      // Use carrier-specific truck operation
      await supabase.rpc('handle_carrier_truck_operation', {
        route_id: routeId,
        carrier_org_id: userOrgs[0].org_id,
        operation_type: 'cancel',
        truck_count: count
      });

      toast({
        title: "Success",
        description: `${count} truck(s) cancelled.`,
      });

      // Refresh load data
      fetchLoadOrder();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel trucks. Please try again.",
        variant: "destructive"
      });
    }
  };

  const fetchLoadOrder = async () => {
    if (!id) return;

    try {
      const { data, error } = await supabase
        .from('load_orders')
        .select(`
          id,
          title,
          description,
          status,
          vehicles_needed,
          vehicles_assigned,
          vehicles_completed,
          vehicles_available,
          cargo_type,
          cargo_weight,
          created_at,
          customer_org_id,
          organizations!customer_org_id (
            name,
            username
          ),
          load_routes (
            id,
            title,
            pickup_city,
            delivery_city,
            pickup_date,
            delivery_date,
            pickup_address,
            delivery_address,
            vehicles_needed,
            vehicles_assigned,
            vehicles_completed,
            vehicles_available,
            status,
            vehicle_type
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        setError('Load order not found');
      } else {
        setLoadOrder(data);
      }
    } catch (err) {
      setError('Failed to fetch load order');
    } finally {
      setLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20">
        <div className="container mx-auto px-4 py-8">
          <Card className="mb-6">
            <CardHeader>
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            </CardContent>
          </Card>
          
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !loadOrder) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/20 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">{t('loadOrders.notFound')}</h3>
            <p className="text-muted-foreground">
              {t('loadOrders.notFoundDescription')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Single Clean Header */}
        <header className="flex items-center justify-between p-3 md:p-6 border-b bg-card">
          {/* Left: Customer Logo + Load Info */}
          <div className="flex items-center gap-2 md:gap-6 min-w-0 flex-1">
            {/* Customer Logo */}
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <div className="w-8 h-8 md:w-12 md:h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center border border-blue-200">
                <Building2 className="h-4 w-4 md:h-6 md:w-6 text-blue-600" />
              </div>
            {/* Customer Info - Always visible */}
            <div className="flex-1 min-w-0">
              <div className="text-sm md:text-lg font-semibold text-foreground">
                {loadOrder.organizations.name}
              </div>
              {loadOrder.organizations.username && (
                <div className="text-xs md:text-sm text-muted-foreground">
                  @{loadOrder.organizations.username}
                </div>
              )}
            </div>
            </div>
            
            {/* Load Info - Responsive layout */}
            <div className="min-w-0 flex-1">
              <h1 className="text-sm md:text-xl font-semibold text-foreground truncate">{loadOrder.title}</h1>
              <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-muted-foreground">
                <span>Ref: {loadOrder.id.substring(0, 8).toUpperCase()}</span>
              </div>
            </div>
          </div>
          
          {/* Right: Settings */}
          <div className="flex-shrink-0 ml-2"><SettingsDropdown /></div>
        </header>

        {/* Main Content Area */}
        <div className="p-6 space-y-6 flex flex-col items-center">
          {/* Routes Grid */}
          <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-1 w-full max-w-none sm:max-w-lg md:max-w-2xl lg:max-w-4xl">
          {loadOrder.load_routes.map((route) => {
            const response = responses[route.id];
            const isAccepted = response?.status === "accepted";
            const remainingTrucks = route.vehicles_available;
            const canAcceptLoad = !isCustomerOrgMember; // Allow both authenticated and unauthenticated users to try

            // For /load context, LoadAcceptanceCard will handle fetching carrier-specific assignments
            // We pass 0 here since the component will fetch and display the correct carrier assignments internally
            const carrierAssignedTrucks = 0; // LoadAcceptanceCard handles this internally

            // Prepare badges based on route status - always show for /load routes
            const badges = [];
            if (isAccepted) {
              badges.push({
                text: t('loadOrders.accepted'),
                variant: "default" as const,
                className: "bg-green-100 text-green-800 border-green-200",
                icon: <CheckCircle className="h-3 w-3 mr-1" />
              });
            }
            if (!response && remainingTrucks === 0) {
              badges.push({
                text: t('loadOrders.fulfilled'),
                variant: "default" as const,
                className: "bg-blue-100 text-blue-800 border-blue-200",
                icon: <CheckCircle className="h-3 w-3 mr-1" />
              });
            }
            if (!response && remainingTrucks > 0) {
              badges.push({
                text: t('loadOrders.available'),
                variant: "default" as const,
                className: "bg-green-50 text-green-600 border-green-200",
                icon: <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse" style={{ animationDuration: '0.8s' }} />,
                animate: true
              });
            }

            return (
              <LoadAcceptanceCard
                key={route.id}
                id={route.id}
                title={route.title}
                subtitle={formatRoute(route.pickup_city, route.delivery_city)}
                loadDetail={{
                  pickupCity: route.pickup_city,
                  dropCity: route.delivery_city,
                  pickupDate: route.pickup_date,
                  vehicleType: route.vehicle_type || 'truck',
                  weightTons: (loadOrder.cargo_weight || 0) / 1000,
                }}
                totalTrucks={route.vehicles_needed}
                availableTrucks={remainingTrucks}
                assignedTrucks={route.vehicles_assigned} // Use actual database value
                trucksCompleted={route.vehicles_completed}
                currentResponse={user && canAcceptLoad ? response : null}
                badges={badges}
                context={canAcceptLoad ? "accept-load" : "view-order"}
                showExpandedAcceptances={expandedAcceptance === route.id}
                onToggleExpandedAcceptances={() => setExpandedAcceptance(expandedAcceptance === route.id ? null : route.id)}
                onStartSelection={handleStartSelection} // Always allow clicking - auth check is inside
                onConfirmAcceptance={() => {}} // Not used - LoadAcceptanceCard handles atomic acceptance internally
                isLoadingAccept={loadingAccept === route.id}
                onUpdateTrucks={user && canAcceptLoad ? updateTrucks : () => {}}
                onCancelSelection={user && canAcceptLoad ? handleCancelSelection : () => {}}
                onRouteDataUpdate={fetchLoadOrder}
                // Clean prop-based control of button visibility
                showAcceptButton={canAcceptLoad}
                showCompleteButton={canAcceptLoad}
                showCancelButton={canAcceptLoad}
                showAvailableLabel={true}
                showMyAssignments={true}
                showRouteAvailabilityBar={canAcceptLoad}
                showRouteAssignedStatusBar={!canAcceptLoad}
                showCarrierRouteStatusBar={false} // Disable carrier status bar
                showFullyAssignedLoadBadge={canAcceptLoad}
                // Complete and Cancel functionality
                onComplete={canAcceptLoad ? handleCompleteRoute : undefined}
                onCancel={canAcceptLoad ? handleCancelRoute : undefined}
              />
            );
           })}
          </div>
        </div>
        
        {/* Footer - Powered by Shohnat */}
        <div className="mt-8 mb-8 px-6 flex justify-center">
          <div className="flex justify-center items-center py-8 px-6 bg-gradient-to-r from-black to-gray-700 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-xl w-full max-w-none sm:max-w-lg md:max-w-2xl lg:max-w-4xl">
            <div className="flex items-center justify-between w-full max-w-md">
              {/* Left: Shohnat Logo */}
              <div className={`flex items-center gap-4 ${user ? '' : 'mx-auto'}`}>
                <div className="flex-shrink-0">
                  <img 
                    src="/lovable-uploads/731a4d5a-546d-41f3-a979-93b9f45689bf.png" 
                    alt="Shohnat" 
                    className="h-10 w-auto" 
                  />
                </div>
              </div>
              
              {/* Right: Back to Dashboard button (only for authenticated users) */}
              {user && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = '/'}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
                >
                  {t('navigation.dashboard')} {language === 'ar' ? '←' : '→'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};